import React from 'react';
import AppIcon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import MetricCard from './MetricCard';
import LoadingCard from './LoadingCard';
import ProgressIndicator from './ProgressIndicator';
import EmptyState from './EmptyState';
import type { GoalStats, ProgressIndicatorData } from '../types';

interface GoalMetricsSectionProps {
  goalStats: GoalStats | null;
  goalIndicators: ProgressIndicatorData[];
  loading: { stats: boolean; indicators: boolean };
  error: string | null;
  onNavigateToGoals: () => void;
  onRetry: () => void;
}

const GoalMetricsSection: React.FC<GoalMetricsSectionProps> = ({
  goalStats,
  goalIndicators,
  loading,
  error,
  onNavigateToGoals,
  onRetry,
}) => {
  const hasGoalData = goalStats !== null;

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-medium text-foreground">Goal Metrics</h3>
        <Button variant="ghost" size="sm" onClick={onNavigateToGoals}>
          View Goals
          <AppIcon name="ArrowRight" size={14} className="ms-1" />
        </Button>
      </div>

      {loading.stats || loading.indicators ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <LoadingCard key={`goal-loading-${index}`} />
          ))}
        </div>
      ) : error && !hasGoalData ? (
        <div className="rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 p-4 text-sm text-red-800 dark:text-red-200 mb-4">
          {error}
        </div>
      ) : hasGoalData ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              title="Company Goals"
              value={goalStats.companyTotalGoals || 0}
              icon="Target"
              iconColor="text-primary"
              subtitle={`${goalStats.companyCompletedGoals || 0} completed`}
            />
            <MetricCard
              title="Goals on Track"
              value={goalStats.companyOnTrackGoals || 0}
              icon="TrendingUp"
              iconColor="text-emerald-500"
              trend={{ type: 'up' }}
              subtitle={`${goalStats.companyAtRiskGoals || 0} at risk`}
            />
            <MetricCard
              title="Personal Goals"
              value={goalStats.userTotalGoals || 0}
              icon="User"
              iconColor="text-violet-500"
              subtitle={`${goalStats.userCompletedGoals || 0} completed`}
            />
            <MetricCard
              title="Engagement Score"
              value={`${goalStats.engagementScore || 0}%`}
              icon="ThumbsUp"
              iconColor="text-blue-500"
              trend={{ type: 'up' }}
              subtitle="Based on goal completion"
            />
          </div>

          {goalIndicators.length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-medium text-foreground mb-3">Progress Indicators</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {goalIndicators.map((indicator) => (
                  <ProgressIndicator key={indicator.id} indicator={indicator} />
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <EmptyState
          icon="Target"
          title="No goal data available"
          description="Goal statistics and progress indicators are not currently loaded."
          actionLabel="Refresh Goals"
          onAction={onRetry}
          variant="goals"
        />
      )}
    </div>
  );
};

export default GoalMetricsSection;
